import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, m } from '../../lib/motion.js';
import { Button } from '../ui/Button.jsx';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * Cookie consent.
 *
 * The store sets three strictly-necessary cookies — a cart id, sign-in tokens,
 * and a CSRF token — and nothing else unless analytics are configured. So this
 * asks about the one thing there is a real choice about and says plainly what
 * the rest are for, rather than presenting a fake choice about cookies that
 * cannot be declined.
 *
 * Declining is a single click of equal weight to accepting. A banner where
 * "reject" is three levels down is not consent.
 */

const STORAGE_KEY = 'ge:cookie-choice';

const read = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage being unavailable means we ask again next visit, which is the
    // conservative outcome rather than assuming consent.
  }
};

export const CookieBanner = () => {
  const [choice, setChoice] = useState('pending');
  const reduced = useReducedMotion();

  useEffect(() => {
    // Read after mount so the banner never appears in a server-rendered or
    // pre-hydration frame and then vanishes.
    setChoice(read() ?? 'ask');
  }, []);

  const decide = (value) => {
    write(value);
    setChoice(value);
  };

  return (
    <AnimatePresence>
      {choice === 'ask' && (
        <m.div
          role="dialog"
          aria-label="Cookie choices"
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: reduced ? 0.15 : 0.3, ease: [0.25, 1, 0.5, 1] }}
          className="fixed inset-x-0 bottom-0 z-50 border-t border-mist bg-surface p-4 shadow-[0_-8px_32px_-16px_rgba(20,23,26,0.25)] sm:inset-x-4 sm:bottom-4 sm:max-w-md sm:border"
        >
          <h2 className="font-mono text-2xs tracking-[0.14em] text-muted uppercase">Cookies</h2>

          <p className="mt-2.5 text-xs leading-relaxed text-muted">
            We set three cookies that the store cannot work without: one to remember your bag, one
            to keep you signed in, and one that stops another site acting on your behalf. Analytics
            are the only optional part, and we do not use advertising cookies at all.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => decide('accepted')}>
              Accept analytics
            </Button>
            <Button size="sm" variant="outline" onClick={() => decide('declined')}>
              Decline
            </Button>
            <Link
              to="/cookie-policy"
              className="ml-auto text-2xs text-muted underline decoration-mist underline-offset-4 hover:text-ink"
            >
              Read the policy
            </Link>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

/** Whether analytics may run. Read by the analytics loader in phase 16. */
export const analyticsAllowed = () => read() === 'accepted';

export default CookieBanner;
